import { z } from 'zod';
import { isoDateTime } from './primitives.js';
/**
 * The admin Observability view: one glance at the API process — its health,
 * the traffic reaching it and the chat assistant's load.
 *
 * The live series come from an in-memory ring buffer in the API process, so
 * they reset on restart and cover this one process only (the worker is not in
 * it). The 24-hour chat block is read from `chat_usage` and survives restarts.
 * Nothing here carries a URL query string, a user id or chat content.
 */
export const observabilityWindow = z.enum(['5m', '15m', '1h']);
export const observabilityQuery = z.object({
    window: observabilityWindow.default('15m'),
});
/** One bucket of the live series. Null where nothing happened to measure. */
export const observabilityPoint = z.object({
    t: isoDateTime,
    requests: z.number().int(),
    errors4xx: z.number().int(),
    errors5xx: z.number().int(),
    p95Ms: z.number().nullable(),
    chatTurns: z.number().int(),
    chatErrors: z.number().int(),
    cpuPct: z.number().nullable(),
    rssBytes: z.number().nullable(),
    eventLoopP99Ms: z.number().nullable(),
});
export const observabilityCheck = z.object({
    ok: z.boolean(),
    latencyMs: z.number().nullable(),
    detail: z.string().optional(),
});
export const observabilityRoute = z.object({
    method: z.string(),
    /** The route pattern (`/api/courses/:courseId`), never the concrete URL. */
    route: z.string(),
    count: z.number().int(),
    errors: z.number().int(),
    p95Ms: z.number().nullable(),
});
export const observabilityResponse = z.object({
    generatedAt: isoDateTime,
    window: observabilityWindow,
    bucketSec: z.number().int(),
    version: z.string(),
    process: z.object({
        startedAt: isoDateTime,
        uptimeSec: z.number(),
        nodeVersion: z.string(),
        /** Process CPU as a share of one core (100 = one core fully busy). */
        cpuPct: z.number().nullable(),
        cpuCores: z.number().int(),
        rssBytes: z.number(),
        heapUsedBytes: z.number(),
        heapTotalBytes: z.number(),
        eventLoopP99Ms: z.number().nullable(),
        loadAvg1m: z.number(),
        systemMemFreeBytes: z.number(),
        systemMemTotalBytes: z.number(),
    }),
    traffic: z.object({
        inFlight: z.number().int(),
        /** Distinct signed-in people who made a request in the last 5 minutes. */
        activeUsers5m: z.number().int(),
        requestsLastMin: z.number().int(),
        requestsWindow: z.number().int(),
        errors5xxWindow: z.number().int(),
        errors4xxWindow: z.number().int(),
        p95MsWindow: z.number().nullable(),
        totalSinceStart: z.number().int(),
    }),
    chat: z.object({
        activeStreams: z.number().int(),
        turnsWindow: z.number().int(),
        errorsWindow: z.number().int(),
        last24h: z.object({
            turns: z.number().int(),
            errors: z.number().int(),
            users: z.number().int(),
            promptTokens: z.number().int(),
            completionTokens: z.number().int(),
            p95LatencyMs: z.number().nullable(),
        }),
        /** Turns per hour for the last 24 hours, oldest first, always 24 entries. */
        hourly: z.array(z.object({ hour: isoDateTime, turns: z.number().int(), errors: z.number().int() })),
    }),
    dependencies: z.object({
        database: observabilityCheck,
        storage: observabilityCheck,
        queue: observabilityCheck,
        dbPool: z.object({ total: z.number().int(), idle: z.number().int(), waiting: z.number().int() }),
    }),
    topRoutes: z.array(observabilityRoute),
    series: z.array(observabilityPoint),
});
/**
 * Daily, weekly and monthly active users, read from the persisted daily
 * activity record (so it survives restarts, unlike the live series). A person
 * is active in a period if they made a signed-in request on any day of it.
 * Periods are calendar periods in `timeZone`; weeks start on Monday.
 */
export const activeUsersGrain = z.enum(['day', 'week', 'month']);
/** A calendar date, YYYY-MM-DD: the first day of the period. */
const calendarDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date as YYYY-MM-DD.')
    .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}, 'Not a real date.');
export const activeUsersPoint = z.object({
    start: calendarDate,
    users: z.number().int(),
});
export const activeUsersResponse = z.object({
    generatedAt: isoDateTime,
    timeZone: z.string(),
    /** The last 60 days, oldest first, today last. */
    daily: z.array(activeUsersPoint),
    /** The last 52 weeks, oldest first, this week last. */
    weekly: z.array(activeUsersPoint),
    /** The last 12 months, oldest first, this month last. */
    monthly: z.array(activeUsersPoint),
});
export const activeUsersListQuery = z.object({
    grain: activeUsersGrain,
    start: calendarDate,
});
export const activeUser = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    /** Days active within the period (always 1 for a day). */
    days: z.number().int(),
    lastActive: calendarDate,
});
export const activeUsersListResponse = z.object({
    grain: activeUsersGrain,
    start: calendarDate,
    /** The last day of the period, inclusive. */
    end: calendarDate,
    users: z.array(activeUser),
});
//# sourceMappingURL=observability.js.map